import { Stack, TextFieldProps } from '@mui/material';
import { Controller, FieldPath, FieldValues, PathValue, useFormContext } from 'react-hook-form';
import { PInputState } from '@/components/atoms/inputState';
import { PTextField } from '@/components/atoms/TextField/PTextField';
import { PTypography } from '@/components/atoms/Typography/PTypography';
import { applyWarningSx } from '@/components/hook-form/utils';
import WarningHelperText from '@/components/hook-form/WarningHelperText';

type Props<TFieldValues extends FieldValues = FieldValues> = Omit<TextFieldProps, 'disabled'> & {
  name: FieldPath<TFieldValues>;
  defaultValue?: PathValue<TFieldValues, FieldPath<TFieldValues>>;
  state?: PInputState;
  /**
   * Setting this will show the character count at the end of the helper text section and the character limit. If you
   * use this you can not set the helperText prop yourself.
   */
  characterLimit?: number;
  onValueChange?: (value: string) => void;
  warningText?: string;
};

/**
 * Minimal@4 component.
 */
export default function RHFTextField<TFieldValues extends FieldValues = FieldValues>({
  name,
  helperText,
  defaultValue,
  state,
  characterLimit = 0,
  onValueChange,
  warningText,
  sx,
  ...other
}: Props<TFieldValues>) {
  const { control, getValues } = useFormContext<TFieldValues>();
  const effectiveSx = applyWarningSx(warningText, sx);

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue}
      render={({ field: { ref, onChange, ...field }, fieldState: { error } }) => (
        <PTextField
          {...field}
          fullWidth
          error={!!error}
          state={state}
          inputRef={ref}
          sx={effectiveSx}
          helperText={
            helperText || warningText || error?.message || characterLimit > 0 ? (
              <Stack spacing={1} direction="row" justifyContent="space-between">
                {!error && warningText ? (
                  <WarningHelperText text={warningText} />
                ) : !error && helperText ? (
                  <PTypography variant="caption" color="secondary">
                    {helperText}
                  </PTypography>
                ) : null}
                {error?.message ? (
                  <PTypography variant="caption" color="error">
                    {error.message}
                  </PTypography>
                ) : null}
                {characterLimit > 0 ? (
                  <PTypography variant="caption" color="secondary">
                    {`${getValues(name)?.length ?? 0}/${characterLimit}`}
                  </PTypography>
                ) : null}
              </Stack>
            ) : null
          }
          onChange={(event) => {
            const value = event.target.value;
            onChange(value);
            onValueChange?.(value);
          }}
          {...other}
        />
      )}
    />
  );
}
